import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { DEFAULT_GATE_DUTY, DEFAULT_INTENSITY } from '../core/limits';
import type { Band, CycleSettings, LogEntry, Profile, Program } from '../core/types';
import { SEED_PROGRAMS } from '../data/programs';
import type { VoiceSample } from '../voice/mapping';
import { KEYS, load, profileKey, remove, save } from './persist';

export type Theme = 'dark' | 'light';

type Store = {
  /* profiles */
  profiles: Profile[];
  profile: Profile;
  switchProfile: (id: string) => void;
  createProfile: (name: string) => string;
  renameProfile: (id: string, name: string) => void;
  setProfileDefaults: (patch: { gateDutyPct?: number; intensityPct?: number }) => void;
  deleteProfile: (id: string) => void;
  setAvoidBands: (bands: Band[]) => void;

  /* programs — scoped to the active profile */
  programs: Program[];
  currentId: string | null;
  current: Program | null;
  select: (id: string | null) => void;
  createProgram: (from?: Partial<Program>) => string;
  updateProgram: (id: string, patch: Partial<Program>) => void;
  updateCycle: (id: string, index: number, patch: Partial<CycleSettings>) => void;
  setCycles: (id: string, cycles: CycleSettings[]) => void;
  deleteProgram: (id: string) => void;
  duplicateProgram: (id: string) => string;
  toggleFavorite: (id: string) => void;

  /* log & voice — scoped to the active profile */
  log: LogEntry[];
  logSession: (program: Program, totalMin: number) => void;
  addNote: (text: string) => void;
  clearLog: () => void;
  voiceSamples: VoiceSample[];
  addVoiceSample: (s: VoiceSample) => void;
  clearVoiceSamples: () => void;

  /* global */
  theme: Theme;
  setTheme: (t: Theme) => void;
  onboarded: boolean;
  completeOnboarding: () => void;
};

const StoreContext = createContext<Store | null>(null);

function uid(prefix = 'p'): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/** Bumped when the shipped library changes; new entries are added, edits kept. */
const SEED_VERSION = 3;

function freshLibrary(): Program[] {
  return SEED_PROGRAMS.map((p) => ({ ...p, cycles: p.cycles.map((c) => ({ ...c })) }));
}

/**
 * Load profiles, migrating a pre-profiles install on first run.
 *
 * Anyone who used the app before profiles existed has programs and notes under
 * the old flat keys. Those become a profile called "Me" rather than being lost.
 */
function loadProfiles(): { profiles: Profile[]; activeId: string } {
  const stored = load<Profile[]>(KEYS.profiles, []);
  if (stored.length > 0) {
    const activeStored = load<string>(KEYS.activeProfile, stored[0].id);
    const active = stored.some((p) => p.id === activeStored) ? activeStored : stored[0].id;
    return { profiles: stored, activeId: active };
  }

  const first: Profile = { id: uid('u'), name: 'Me', avoidBands: [], createdAt: Date.now() };

  const legacyPrograms = load<Program[] | null>(KEYS.legacyPrograms, null);
  if (legacyPrograms) {
    save(profileKey(first.id, 'programs'), legacyPrograms);
    save(profileKey(first.id, 'log'), load<LogEntry[]>(KEYS.legacyLog, []));
    save(profileKey(first.id, 'voice'), load<VoiceSample[]>(KEYS.legacyVoice, []));
    remove(KEYS.legacyPrograms);
    remove(KEYS.legacyLog);
    remove(KEYS.legacyVoice);
  }

  save(KEYS.profiles, [first]);
  save(KEYS.activeProfile, first.id);
  return { profiles: [first], activeId: first.id };
}

/*
 * Fill in fields added to CycleSettings after a program was saved.
 *
 * gateDutyPct arrived with the one-duty-cycle change and had no migration, and
 * loadPrograms() only ever APPENDS newly shipped entries — it never touches
 * stored objects. So every program saved before then came back without the
 * field, and the editor rendered the literal string "undefined" in the duty box
 * and "NaN ms pulse, then NaN ms silent" beneath it. The device was fine
 * (buildCommands falls back to 50), which is what kept it hidden.
 */
function migrateCycles(p: Program): Program {
  if (p.cycles.every((c) => typeof c.gateDutyPct === 'number')) return p;
  return {
    ...p,
    cycles: p.cycles.map((c) => ({ ...c, gateDutyPct: c.gateDutyPct ?? DEFAULT_GATE_DUTY })),
  };
}

/** Programs for a profile, topped up with any newly shipped library entries. */
function loadPrograms(profileId: string): Program[] {
  const key = profileKey(profileId, 'programs');
  const stored = load<Program[] | null>(key, null);
  if (stored === null) {
    save(KEYS.seedVersion, SEED_VERSION);
    return freshLibrary();
  }
  const migrated = stored.map(migrateCycles);
  if (load<number>(KEYS.seedVersion, 0) >= SEED_VERSION) return migrated;

  const existing = new Set(migrated.map((p) => p.id));
  const additions = freshLibrary().filter((p) => !existing.has(p.id));
  save(KEYS.seedVersion, SEED_VERSION);
  return [...migrated, ...additions];
}

export const BLANK_CYCLE: CycleSettings = {
  onMin: 20,
  offMin: 0,
  carrierHz: 300,
  intensity: DEFAULT_INTENSITY,
  sweepEnabled: false,
  gateHz: 10,
  sweepLo: 8,
  sweepHi: 12,
  sweepHold: 10,
  sweepTrue: true,
  gateDutyPct: DEFAULT_GATE_DUTY,
};

export function StoreProvider({ children }: { children: ReactNode }) {
  const [{ profiles: initialProfiles, activeId }] = useState(loadProfiles);
  const [profiles, setProfiles] = useState<Profile[]>(initialProfiles);
  const [profileId, setProfileId] = useState<string>(activeId);

  const [programs, setPrograms] = useState<Program[]>(() => loadPrograms(activeId));
  const [log, setLog] = useState<LogEntry[]>(() => load<LogEntry[]>(profileKey(activeId, 'log'), []));
  const [voiceSamples, setVoiceSamples] = useState<VoiceSample[]>(() =>
    load<VoiceSample[]>(profileKey(activeId, 'voice'), []),
  );

  const [currentId, setCurrentId] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Theme>(() => load<Theme>(KEYS.theme, 'light'));
  const [onboarded, setOnboarded] = useState<boolean>(() => load<boolean>(KEYS.onboarded, false));

  useEffect(() => save(KEYS.profiles, profiles), [profiles]);
  useEffect(() => save(KEYS.activeProfile, profileId), [profileId]);
  useEffect(() => save(profileKey(profileId, 'programs'), programs), [programs, profileId]);
  useEffect(() => save(profileKey(profileId, 'log'), log), [log, profileId]);
  useEffect(() => save(profileKey(profileId, 'voice'), voiceSamples), [voiceSamples, profileId]);
  useEffect(() => save(KEYS.theme, theme), [theme]);
  useEffect(() => save(KEYS.onboarded, onboarded), [onboarded]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const profile = useMemo(
    () => profiles.find((p) => p.id === profileId) ?? profiles[0],
    [profiles, profileId],
  );

  const switchProfile = useCallback((id: string) => {
    setProfileId(id);
    setPrograms(loadPrograms(id));
    setLog(load<LogEntry[]>(profileKey(id, 'log'), []));
    setVoiceSamples(load<VoiceSample[]>(profileKey(id, 'voice'), []));
    setCurrentId(null);
  }, []);

  const createProfile = useCallback(
    (name: string) => {
      const next: Profile = {
        id: uid('u'),
        name: name.trim() || 'New profile',
        avoidBands: [],
        defaultGateDutyPct: DEFAULT_GATE_DUTY,
        defaultIntensityPct: DEFAULT_INTENSITY,
        createdAt: Date.now(),
      };
      // Seed the new profile with its own copy of the library.
      save(profileKey(next.id, 'programs'), freshLibrary());
      setProfiles((prev) => [...prev, next]);
      switchProfile(next.id);
      return next.id;
    },
    [switchProfile],
  );

  const setProfileDefaults = useCallback(
    (patch: { gateDutyPct?: number; intensityPct?: number }) => {
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === profileId
            ? {
                ...p,
                ...(patch.gateDutyPct !== undefined
                  ? { defaultGateDutyPct: patch.gateDutyPct }
                  : {}),
                ...(patch.intensityPct !== undefined
                  ? { defaultIntensityPct: patch.intensityPct }
                  : {}),
              }
            : p,
        ),
      );
    },
    [profileId],
  );

  const renameProfile = useCallback((id: string, name: string) => {
    setProfiles((prev) => prev.map((p) => (p.id === id ? { ...p, name: name.trim() || p.name } : p)));
  }, []);

  const deleteProfile = useCallback(
    (id: string) => {
      setProfiles((prev) => {
        // Never leave zero profiles — there would be nothing to switch back to.
        if (prev.length <= 1) return prev;
        const next = prev.filter((p) => p.id !== id);
        remove(profileKey(id, 'programs'));
        remove(profileKey(id, 'log'));
        remove(profileKey(id, 'voice'));
        if (id === profileId) switchProfile(next[0].id);
        return next;
      });
    },
    [profileId, switchProfile],
  );

  const setAvoidBands = useCallback(
    (bands: Band[]) => {
      setProfiles((prev) => prev.map((p) => (p.id === profileId ? { ...p, avoidBands: bands } : p)));
    },
    [profileId],
  );

  const appendLog = useCallback((entry: LogEntry) => {
    setLog((prev) => [entry, ...prev].slice(0, 2000));
  }, []);

  const createProgram = useCallback((from?: Partial<Program>) => {
    const id = uid();
    // A new step starts from this profile's preferences, not a fixed constant.
    const seed: CycleSettings = {
      ...BLANK_CYCLE,
      gateDutyPct: profile?.defaultGateDutyPct ?? DEFAULT_GATE_DUTY,
      intensity: profile?.defaultIntensityPct ?? BLANK_CYCLE.intensity,
    };
    const next: Program = {
      id,
      name: 'NEW',
      title: 'New program',
      cycles: [seed],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      ...from,
    };
    setPrograms((prev) => [next, ...prev]);
    setCurrentId(id);
    return id;
  }, [profile]);

  const updateProgram = useCallback((id: string, patch: Partial<Program>) => {
    setPrograms((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch, updatedAt: Date.now() } : p)),
    );
  }, []);

  const updateCycle = useCallback((id: string, index: number, patch: Partial<CycleSettings>) => {
    setPrograms((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const cycles = p.cycles.map((c, i) => (i === index ? { ...c, ...patch } : c));
        return { ...p, cycles, updatedAt: Date.now() };
      }),
    );
  }, []);

  const setCycles = useCallback((id: string, cycles: CycleSettings[]) => {
    setPrograms((prev) =>
      prev.map((p) => (p.id === id ? { ...p, cycles, updatedAt: Date.now() } : p)),
    );
  }, []);

  const deleteProgram = useCallback((id: string) => {
    setPrograms((prev) => prev.filter((p) => p.id !== id));
    setCurrentId((cur) => (cur === id ? null : cur));
  }, []);

  const duplicateProgram = useCallback(
    (id: string) => {
      const source = programs.find((p) => p.id === id);
      const newId = uid();
      if (!source) return newId;
      setPrograms((prev) => [
        {
          ...source,
          id: newId,
          name: `${source.name}_C`.slice(0, 12),
          title: `${source.title ?? source.name} copy`,
          favorite: false,
          cycles: source.cycles.map((c) => ({ ...c })),
          createdAt: Date.now(),
          updatedAt: Date.now(),
        },
        ...prev,
      ]);
      setCurrentId(newId);
      return newId;
    },
    [programs],
  );

  const toggleFavorite = useCallback((id: string) => {
    setPrograms((prev) =>
      prev.map((p) => (p.id === id ? { ...p, favorite: !p.favorite, updatedAt: Date.now() } : p)),
    );
  }, []);

  const logSession = useCallback(
    (program: Program, totalMin: number) => {
      appendLog({
        kind: 'session',
        at: Date.now(),
        programName: program.title ?? program.name,
        cycles: program.cycles.map((c) => ({ ...c })),
        totalMin,
      });
    },
    [appendLog],
  );

  const addNote = useCallback(
    (text: string) => appendLog({ kind: 'note', at: Date.now(), text }),
    [appendLog],
  );

  const value = useMemo<Store>(
    () => ({
      profiles,
      profile,
      switchProfile,
      createProfile,
      renameProfile,
      setProfileDefaults,
      deleteProfile,
      setAvoidBands,
      programs,
      currentId,
      current: programs.find((p) => p.id === currentId) ?? null,
      select: setCurrentId,
      createProgram,
      updateProgram,
      updateCycle,
      setCycles,
      deleteProgram,
      duplicateProgram,
      toggleFavorite,
      log,
      logSession,
      addNote,
      clearLog: () => setLog([]),
      voiceSamples,
      addVoiceSample: (s: VoiceSample) => setVoiceSamples((prev) => [s, ...prev].slice(0, 200)),
      clearVoiceSamples: () => setVoiceSamples([]),
      theme,
      setTheme: setThemeState,
      onboarded,
      completeOnboarding: () => setOnboarded(true),
    }),
    [
      profiles,
      profile,
      switchProfile,
      createProfile,
      renameProfile,
      setProfileDefaults,
      deleteProfile,
      setAvoidBands,
      programs,
      currentId,
      createProgram,
      updateProgram,
      updateCycle,
      setCycles,
      deleteProgram,
      duplicateProgram,
      toggleFavorite,
      log,
      logSession,
      addNote,
      voiceSamples,
      theme,
      onboarded,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used inside StoreProvider');
  return ctx;
}
